import { useEffect, useMemo, useState } from "react";
import { State } from "../../../../../redux/slices";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { DColumns } from "../../../../../redux/actions/data/DataDeclarations";
import { userProfiles } from "../../../../../redux/actions/users/managerAsyncActions/managerAsyncActions";
import { ManagerUsersConfigRows } from "../../../../../redux/slices/users/createUsers";

import { GridActionsCellItem, GridRowId } from "@mui/x-data-grid-pro";

import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import SearchIcon from "@mui/icons-material/Search";
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  debounce,
  FormControl,
  InputAdornment,
  InputLabel,
  ListSubheader,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";

import { getLocalizedTextByLocaleVersionTwo } from "../../../../../utils/localized-text";
import { selectLocale } from "../../../../../redux/slices/layout";

import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { stylesMUIManagers, useStyles } from "../styles/managersCreationStyles";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const containsText = (text: string, searchText: string) =>
  text.toLowerCase().indexOf(searchText.toLowerCase()) > -1;

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 350,
    },
  },
};

const CreateManagerUsersColumnsRows = (
  rawColumns: DColumns[],

  handleDeleteClick: (
    id: GridRowId,
    managerUsersConfigRows: ManagerUsersConfigRows[],
    setManagerRows: (value: ManagerUsersConfigRows[]) => void,
  ) => () => void,

  handleChangeGroups: (
    event: SelectChangeEvent<any>,
    id: number,
    managerUsersConfigRows: ManagerUsersConfigRows[],
    setManagerRows: (value: ManagerUsersConfigRows[]) => void,
    groups: any[],
  ) => void,

  handleChangeEmail: (
    event: any,
    id: string,
    managerUsersConfigRows: ManagerUsersConfigRows[],
    setManagerRows: (value: ManagerUsersConfigRows[]) => void,
  ) => void,

  handleChangeRole: (
    event: any,
    id: number,
    managerUsersConfigRows: ManagerUsersConfigRows[],
    setManagerRows: (value: ManagerUsersConfigRows[]) => void,
  ) => void,

  handleSiteChange: (
    locationID: number,
    locationName: string,
    id: number,
    managerUsersConfigRows: ManagerUsersConfigRows[],
    setManagerRows: (value: ManagerUsersConfigRows[]) => void,
  ) => void,

  handleSiteChangeToNull: (
    id: number,
    managerUsersConfigRows: ManagerUsersConfigRows[],
    setManagerRows: (value: ManagerUsersConfigRows[]) => void,
  ) => void,

  setManagerRows: (value: ManagerUsersConfigRows[]) => void,
) => {
  const [t] = useTranslation("global");
  const classes = useStyles();
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const { groups, locations } = useSelector(
    (state: State) => state.LocationsReducer,
  );
  const { user } = useSelector((state: State) => state.Authentication);
  const token = user?.token ?? "";
  const { tryingToCreateSomething, userRoles, managerUsersConfigRows } =
    useSelector((state: State) => state.CreateManagersReducer);
  const locale = useSelector(selectLocale);
  const [searchText, setSearchText] = useState("");

  const displayedOptionsActions = useMemo(
    () => groups.filter((option: any) => containsText(option.name, searchText)),
    [searchText],
  );

  const filteruserRoles = userRoles?.filter(
    (role: any) => role.id === 4 || role.id === 3,
  );

  useEffect(() => {
    dispatch(userProfiles(token));
  }, []);

  const columnNames = {
    id: t("Data.table.Details"),
    email: t("Users_managers.email"),
    role: t("Users_tables.columns_table_one.role"),
    assignedGroups: t("Users_managers.groups"),
    actions: "actions",
  };

  let columns: any[] = rawColumns.map((column: DColumns) => {
    if (column.field === "assignedGroups") {
      return {
        field: column.field,
        headerName: (columnNames as never)[column.field],
        minWidth: 300,
        type: "string",
        sortable: false,
        disableColumnMenu: true,
        alwaysHide: column.alwaysHide,
        renderCell: (params: any) => {
          const handleRoleChange = (event: any) => {
            handleChangeGroups(
              event,
              params.id,
              managerUsersConfigRows,
              setManagerRows,
              groups,
            );
          };
          return (
            <FormControl
              className={classes.root}
              sx={stylesMUIManagers.columnsFormControl}
            >
              <InputLabel sx={stylesMUIManagers.InputLabel}>
                {params.value.length === 0 ? t("Common_options.select") : ""}
              </InputLabel>
              <Select
                multiple
                value={params.value}
                onChange={handleRoleChange}
                input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
                renderValue={(selected) => (
                  <Box sx={stylesMUIManagers.columnsFormControlBox}>
                    {selected.map((roleId: any) => (
                      <Chip
                        key={roleId}
                        label={groups?.find((e) => e.id === roleId)?.name}
                      />
                    ))}
                  </Box>
                )}
                MenuProps={MenuProps}
              >
                <ListSubheader>
                  <TextField
                    size="small"
                    value={searchText}
                    autoFocus
                    placeholder={t("Common_options.type_to_search")}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    onChange={(e) => setSearchText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key !== "Escape") {
                        e.stopPropagation();
                      }
                    }}
                  />
                </ListSubheader>

                {displayedOptionsActions.map((option: any, i: number) => (
                  <MenuItem key={option.id} value={option.id}>
                    <Checkbox
                      value={option?.id}
                      checked={params?.value?.includes(option.id)}
                    />
                    {option?.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        },
      };
    }
    if (column.field === "email") {
      return {
        field: column.field,
        headerName: (columnNames as never)[column.field],
        minWidth: 220,
        type: "string",
        sortable: false,
        disableColumnMenu: true,
        alwaysHide: column.alwaysHide,
        renderCell: (params: { value: string; id: string }) => {
          const handleChange = (event: any) => {
            const debounced = debounce(() => {
              handleChangeEmail(
                event,
                params.id,
                managerUsersConfigRows,
                setManagerRows,
              );
            }, 500);

            debounced();
          };

          return <TextField onChange={handleChange} />;
        },
      };
    }

    if (column.field === "assignedSite") {
      return {
        field: column.field,
        headerName: (columnNames as never)[column.field],
        width: 220,
        alwaysHide: column.alwaysHide,
        disableColumnMenu: true,
        sortable: false,

        renderCell: (params: any) => {
          const paramsRow = params;

          const handleSite = (locationID: number, locationName: string) => {
            handleSiteChange(
              locationID,
              locationName,
              params.id,
              managerUsersConfigRows,
              setManagerRows,
            );
          };

          return (
            <Autocomplete
              sx={stylesMUIManagers.assignedSiteAutocomplete}
              value={params.value}
              options={locations}
              fullWidth
              getOptionLabel={(option) => option.name}
              onChange={(_e, value, reason) => {
                if (reason === "clear" || reason === "removeOption")
                  handleSiteChangeToNull(
                    params.id,
                    managerUsersConfigRows,
                    setManagerRows,
                  ); ///<=== problem here
                if (reason === "selectOption")
                  handleSite(value?.id as any, value?.name as any);
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderOption={(props, option, { selected }) => {
                const { ...optionProps } = props;
                return (
                  <MenuItem {...optionProps}>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {option.name}
                  </MenuItem>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={
                    paramsRow.value?.length === 0
                      ? t("Common_options.select")
                      : ""
                  }
                />
              )}
            />
          );
        },
      };
    }

    if (column.field === "role") {
      return {
        field: column.field,
        headerName: (columnNames as never)[column.field],
        minWidth: 220,
        type: "string",
        sortable: false,
        disableColumnMenu: true,
        alwaysHide: column.alwaysHide,
        renderCell: (params: any) => {
          const handleChange = (event: any) => {
            handleChangeRole(
              event,
              params.id,
              managerUsersConfigRows,
              setManagerRows,
            );
          };

          return (
            <FormControl
              className={
                !tryingToCreateSomething
                  ? classes.singleSelect
                  : classes.singleSelectError
              }
              sx={{ m: 1, width: 300, border: "none" }}
              fullWidth
            >
              <InputLabel sx={{ marginLeft: "-3px" }}>
                {params.value.length === 0 ? t("Common_options.select") : ""}
              </InputLabel>
              <Select value={params.value} label="role" onChange={handleChange}>
                {filteruserRoles.map((roles: any) => (
                  <MenuItem value={roles.id}>
                    {`${getLocalizedTextByLocaleVersionTwo(
                      locale,
                      roles.name.localizedTexts,
                    )}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        },
      };
    }

    if (column.field === "actions") {
      return {
        headerName: "",
        field: column.field,
        width: 50,
        hide: !column.show,
        filterable: false,
        sortable: false,
        alwaysHide: false,
        disableColumnMenu: true,
        cellClassName: "actions",
        renderCell: ({ id }: any) => {
          return [
            <GridActionsCellItem
              key={id}
              icon={<DeleteIcon />}
              label="Delete"
              onClick={handleDeleteClick(
                id,
                managerUsersConfigRows,
                setManagerRows,
              )}
              color="inherit"
            />,
          ];
        },
      };
    }

    return {
      field: column.field,
      headerName: (columnNames as never)[column.field],
      width: column.width,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
      alwaysHide: column.alwaysHide,
      editable: true,
    };
  });

  const columnsFilter = columns?.filter((columns: any) => !columns.alwaysHide);

  return {
    rawColumns: columnsFilter,
  };
};

export default CreateManagerUsersColumnsRows;
